import { Mascot } from './Mascot';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Account, Attachment, ComposeMode, Folder, MessageAction, MessageDetail, ThreadView } from '@momomail/shared';
import { api, errorText } from '../lib/bridge';
import { markerInFrame } from '../lib/theme';
import { displayName, fileSize, folderName, formatAddress, listSep, longDate, shortDate } from '../lib/format';
import { Icon, type IconName } from './Icon';
import { AiEventCard, runUnsubscribe, suggestsUnsubscribe } from './AiButtons';
import { InviteCard, openAddToCalendar } from './Calendar';
import { Avatar } from './MessageList';
import { confirmDialog, Popover } from './Overlay';
import { toast } from './Toasts';
import { t, tMessage } from '../lib/i18n';
import { kit, screenLoading } from '../lib/screen';

/**
 * Email body: rendered in a sandboxed iframe (no allow-scripts, so not a single line of script in the email can run),
 * with a strict CSP in the body itself; height follows the content; clicked links go to the system browser (mailto composes in this app).
 */
function EmailFrame({ html, title }: { html: string; title: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(80);
  const measureRef = useRef<() => void>(() => {});

  // Re-measure the height when the width changes (window resize, sidebar changes). Only observe the iframe element itself in the parent page.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let width = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== width) {
        width = el.clientWidth;
        measureRef.current();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onLoad = useCallback(() => {
    const doc = ref.current?.contentDocument;
    if (!doc) return;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      // Measure body rather than documentElement: the latter is at least as tall as the iframe currently is, so it can only grow, never shrink
      frame = requestAnimationFrame(() => setHeight(Math.max(40, Math.ceil(doc.body.getBoundingClientRect().height) + 4)));
    };
    measureRef.current = measure;
    // Height always equals content height, so the iframe itself needs no scrollbar
    doc.documentElement.style.overflow = 'hidden';
    measure();
    for (const img of Array.from(doc.images)) {
      img.addEventListener('load', measure);
      img.addEventListener('error', measure);
    }
    // Measure again when fonts and styles arrive late
    setTimeout(measure, 300);
    // If "全部用馍馍字体" (use Momo font everywhere) is chosen in settings: use it for the body too (re-measure once the font arrives)
    if (document.documentElement.dataset.font === 'all') void markerInFrame(doc).then(() => doc.fonts.ready).then(measure);
    doc.addEventListener('click', (e) => {
      const a = (e.target as HTMLElement).closest('a');
      if (!a) return;
      e.preventDefault();
      const href = a.getAttribute('href');
      if (href && /^(https?:|mailto:)/i.test(href)) void api().mail.openExternal(href);
    });
  }, []);
  return <iframe ref={ref} className="email-frame" title={title} sandbox="allow-same-origin" srcDoc={html} style={{ height }} onLoad={onLoad} />;
}

function AttachmentChip({ messageId, a }: { messageId: number; a: Attachment }) {
  const [busy, setBusy] = useState<'open' | 'save' | null>(null);
  const ext = a.filename.split('.').pop()?.toUpperCase().slice(0, 4) ?? '';
  const run = async (kind: 'open' | 'save') => {
    setBusy(kind);
    try {
      if (kind === 'open') await api().mail.openAttachment(messageId, a.index, a.filename);
      else if (await api().mail.saveAttachment(messageId, a.index, a.filename)) toast(t('已保存 {name}', { name: a.filename }), 'ok');
    } catch (error) {
      toast(errorText(error), 'error');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="attachment">
      <span className="attachment__ext" aria-hidden>
        {a.contentType.startsWith('image/') ? <Icon name="image" size={18} /> : ext || <Icon name="file" size={18} />}
      </span>
      <span className="attachment__text">
        <span className="attachment__name" title={a.filename}>
          {a.filename}
        </span>
        <span className="attachment__size">{fileSize(a.size)}</span>
      </span>
      <button className="btn btn--icon btn--quiet" title={t('打开')} aria-label={t('打开 {name}', { name: a.filename })} disabled={!!busy} onClick={() => void run('open')}>
        {busy === 'open' ? <span className="spinner" /> : <Icon name="external" size={15} />}
      </button>
      <button className="btn btn--icon btn--quiet" title={t('另存为…')} aria-label={t('保存 {name}', { name: a.filename })} disabled={!!busy} onClick={() => void run('save')}>
        {busy === 'save' ? <span className="spinner" /> : <Icon name="download" size={15} />}
      </button>
    </div>
  );
}

function MessageCard({
  m,
  expanded,
  onToggle,
  onReply,
  me,
  bodyKey,
}: {
  m: MessageDetail;
  expanded: boolean;
  onToggle: () => void;
  onReply: (mode: ComposeMode, id: number) => void;
  me: string | undefined;
  /** The opened message's: its body is thread.body for 馍馍. */
  bodyKey?: string;
}) {
  const [details, setDetails] = useState(false);
  const recipients = (list: { name: string; address: string }[]) => list.map((a) => (me && a.address.toLowerCase() === me.toLowerCase() ? t('我') : displayName(a))).join(listSep());
  if (!expanded) {
    return (
      <button className="msg msg--collapsed" onClick={onToggle} aria-expanded={false}>
        <Avatar name={m.from.name} address={m.from.address} size={30} />
        <span className="msg__from">{displayName(m.from)}</span>
        <span className="msg__peek">{m.snippet || m.subject}</span>
        <span className="msg__date">{shortDate(m.date)}</span>
      </button>
    );
  }
  const files = m.attachments.filter((a) => !a.inline);
  // For 馍馍: the body is in a frame it can't see into, so it reads the mail's text instead
  const body = (el: HTMLDivElement | null) =>
    el
      ? kit.register(el, {
          name: 'The mail',
          ...(bodyKey ? { key: bodyKey } : {}),
          role: 'text',
          description: `From ${formatAddress(m.from)}: "${m.subject}" (message #${m.id}). Read it for its text`,
          ops: [{ name: 'read', description: 'Its text', run: () => m.text }],
        })
      : undefined;
  return (
    <article className="msg" aria-label={t('{name} 的邮件', { name: displayName(m.from) })}>
      <header className="msg__head">
        <Avatar name={m.from.name} address={m.from.address} size={40} />
        <div className="msg__who">
          <button className="msg__from-line" onClick={onToggle} title={t('收起')}>
            <strong>{displayName(m.from)}</strong>
            <span className="msg__addr">{m.from.address}</span>
          </button>
          <button className="msg__to" onClick={() => setDetails((v) => !v)} aria-expanded={details}>
            {t('发给')} {recipients(m.to) || t('(无)')}
            {m.cc.length > 0 && t('，抄送 {names}', { names: recipients(m.cc) })}
            <Icon name="down" size={13} className={details ? 'is-open' : ''} />
          </button>
        </div>
        <div className="msg__meta">
          <time dateTime={new Date(m.date).toISOString()} title={longDate(m.date)}>
            {shortDate(m.date)}
          </time>
          <button className="btn btn--icon btn--quiet" title={t('回复')} aria-label={t('回复这封')} onClick={() => onReply('reply', m.id)}>
            <Icon name="reply" size={15} />
          </button>
        </div>
      </header>
      {details && (
        <dl className="msg__details">
          <dt>{t('发件人')}</dt>
          <dd>{formatAddress(m.from)}</dd>
          <dt>{t('收件人')}</dt>
          <dd>{m.to.map(formatAddress).join(', ') || t('(无)')}</dd>
          {m.cc.length > 0 && (
            <>
              <dt>{t('抄送')}</dt>
              <dd>{m.cc.map(formatAddress).join(', ')}</dd>
            </>
          )}
          {m.bcc.length > 0 && (
            <>
              <dt>{t('密送')}</dt>
              <dd>{m.bcc.map(formatAddress).join(', ')}</dd>
            </>
          )}
          <dt>{t('时间')}</dt>
          <dd>{longDate(m.date)}</dd>
          <dt>{t('位置')}</dt>
          <dd>{m.folderRole ? folderName({ role: m.folderRole, name: m.folderName }) : m.folderName}</dd>
        </dl>
      )}
      {m.invite && <InviteCard messageId={m.id} invite={m.invite} />}
      {/* The event the AI found (an invite has its own card above) */}
      {!m.invite && m.aiTag?.event && <AiEventCard key={`ai-event-${m.id}`} messageId={m.id} tag={m.aiTag} />}
      {m.unsubscribe && <UnsubscribeBar key={`unsub-${m.id}`} message={m} />}
      {m.bodyMissing ? (
        <div className="notice notice--warn msg__missing">
          <Icon name="offline" size={16} /> {t('正文还没下载下来（可能离线了）。连上网络后再打开这封试试。')}
        </div>
      ) : m.html ? (
        <div className="sheet" ref={body}>
          <EmailFrame html={m.html} title={t('邮件正文：{subject}', { subject: m.subject })} />
        </div>
      ) : (
        <div className="sheet sheet--empty muted" ref={body}>{t('（这封邮件没有正文）')}</div>
      )}
      {files.length > 0 && (
        <div className="attachments">
          <div className="attachments__label">
            <Icon name="clip" size={14} /> {files.length === 1 ? t('1 个附件') : t('{n} 个附件', { n: files.length })}
          </div>
          <div className="attachments__grid">
            {files.map((a) => (
              <AttachmentChip key={a.index} messageId={m.id} a={a} />
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export function Reader({
  messageId,
  conversation,
  account,
  folders,
  refreshKey,
  onReply,
  onAction,
  onEditDraft,
}: {
  messageId: number;
  conversation: boolean;
  account: Account | undefined;
  folders: Folder[];
  refreshKey: number;
  onReply: (mode: ComposeMode, id: number) => void;
  onAction: (action: MessageAction, ids: number[]) => void;
  onEditDraft: (id: number) => void;
}) {
  const [thread, setThread] = useState<ThreadView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allowRemote, setAllowRemote] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const lastId = useRef<number | null>(null);

  useEffect(() => {
    let alive = true;
    const switched = lastId.current !== messageId;
    if (switched) {
      setThread(null);
      setAllowRemote(false);
      setError(null);
    }
    lastId.current = messageId;
    const remote = switched ? false : allowRemote;
    void screenLoading(api().mail.thread(messageId, conversation, remote))
      .then((view) => {
        if (!alive) return;
        setThread(view);
        setError(null);
        if (switched) {
          // Expanded: the opened message, the last one, and unread ones
          const open = new Set<number>([messageId]);
          const last = view.messages.at(-1);
          if (last) open.add(last.id);
          for (const m of view.messages) if (!m.seen) open.add(m.id);
          setExpanded(open);
        }
      })
      .catch((e) => alive && setError(errorText(e)));
    return () => {
      alive = false;
    };
    // When allowRemote changes, showImages handles reloading itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, conversation, refreshKey]);

  const showImages = () => {
    setAllowRemote(true);
    void api()
      .mail.thread(messageId, conversation, true)
      .then(setThread)
      .catch((e) => toast(errorText(e), 'error'));
  };

  const current = thread?.messages.find((m) => m.id === messageId) ?? thread?.messages.at(-1);
  const anyUnread = thread?.messages.some((m) => !m.seen) ?? false;
  const flagged = thread?.messages.some((m) => m.flagged) ?? false;
  const remoteBlocked = !allowRemote && (thread?.messages.some((m) => m.hasRemoteImages) ?? false);
  const role = current?.folderRole ?? null;
  const moveTargets = folders.filter((f) => f.accountId === current?.accountId && f.selectable && f.id !== current?.folderId);

  const tool = (icon: IconName, label: string, onClick: () => void, opts: { hint?: string; active?: boolean; danger?: boolean; text?: boolean; afford?: string; policy?: 'confirm' } = {}) => (
    <button data-afford-key={opts.afford} data-afford-policy={opts.policy} className={`btn btn--small btn--quiet tool ${opts.active ? 'is-active' : ''} ${opts.danger ? 'tool--danger' : ''}`} title={opts.hint ? t('{label}（{hint}）', { label, hint: opts.hint }) : label} aria-label={label} onClick={onClick}>
      <Icon name={icon} size={16} fill={icon === 'star' && !!opts.active} />
      {opts.text && <span className="tool__text">{label}</span>}
    </button>
  );

  return (
    <section className="pane reader" aria-label={t('阅读邮件')}>
      <div className="reader__toolbar" role="toolbar" aria-label={t('邮件操作')}>
        {tool('reply', t('回复'), () => onReply('reply', messageId), { hint: 'R', text: true, afford: 'thread.reply' })}
        {tool('replyAll', t('全部回复'), () => onReply('replyAll', messageId), { hint: 'A', text: true, afford: 'thread.reply_all' })}
        {tool('forward', t('转发'), () => onReply('forward', messageId), { hint: 'F', text: true, afford: 'thread.forward' })}
        <i className="reader__sep" />
        {role !== 'archive' && tool('archive', t('归档'), () => onAction({ kind: 'archive' }, [messageId]), { hint: 'E', afford: 'thread.archive' })}
        {tool('trash', role === 'trash' ? t('彻底删除') : t('删除'), () => onAction({ kind: 'trash' }, [messageId]), { hint: 'Delete', danger: role === 'trash', afford: 'thread.delete', ...(role === 'trash' ? { policy: 'confirm' as const } : {}) })}
        {role !== 'spam' && role !== 'sent' && role !== 'drafts' && tool('spam', t('标为垃圾邮件'), () => onAction({ kind: 'spam' }, [messageId]), { afford: 'thread.spam' })}
        <span className="reader__move">
          <button className="btn btn--small btn--quiet tool" data-afford-key="thread.move" data-popover-anchor title={t('移动到…（V）')} aria-label={t('移动到')} aria-haspopup="menu" aria-expanded={moveOpen} onClick={() => setMoveOpen((v) => !v)}>
            <Icon name="move" size={16} />
          </button>
          <Popover open={moveOpen} onClose={() => setMoveOpen(false)} className="move-menu">
            <div className="popover__title">{t('移动到')}</div>
            {moveTargets.map((f) => (
              <button
                key={f.id}
                role="menuitem"
                className="popover__item"
                onClick={() => {
                  setMoveOpen(false);
                  onAction({ kind: 'move', folderId: f.id }, [messageId]);
                }}
              >
                <Icon name={f.role === 'inbox' ? 'inbox' : f.role === 'trash' ? 'trash' : f.role === 'archive' ? 'archive' : 'folder'} size={15} />
                <span>{folderName(f)}</span>
              </button>
            ))}
          </Popover>
        </span>
        <i className="reader__sep" />
        {tool(anyUnread ? 'mailOpen' : 'mail', anyUnread ? t('标为已读') : t('标为未读'), () => onAction({ kind: 'seen', value: anyUnread }, [messageId]), { hint: 'U', afford: 'thread.seen' })}
        {tool('star', flagged ? t('取消星标') : t('加星标'), () => onAction({ kind: 'flagged', value: !flagged }, [messageId]), { hint: 'S', active: flagged, afford: 'thread.star' })}
      </div>

      <div className="reader__scroll">
        {error && (
          <div className="reader__state">
            <div className="notice notice--error">{error}</div>
          </div>
        )}
        {!error && !thread && (
          <div className="reader__state" aria-busy>
            <span className="spinner spinner--big" />
            <span className="muted">{t('正在打开…')}</span>
          </div>
        )}
        {thread && current && (
          // Keyed by message: opening another one replays the short slide-in
          <div key={messageId} className="reader__enter">
            <div className="reader__head">
              <h1 className="reader__subject">{thread.subject || t('(无主题)')}</h1>
              <div className="reader__tags">
                {account && (
                  <span className="tag tag--dim" title={account.email}>
                    <i className="tag__dot" style={{ background: account.color }} />
                    {account.name}
                  </span>
                )}
                {current.folderRole !== 'inbox' && <span className="tag tag--dim">{current.folderRole ? folderName({ role: current.folderRole, name: current.folderName }) : current.folderName}</span>}
                {thread.messages.length > 1 && <span className="tag">{t('{n} 封', { n: thread.messages.length })}</span>}
                <button className="btn btn--small btn--quiet reader__cal" title={t('从这封邮件建一个日程')} onClick={() => openAddToCalendar({ messageId: current.id })}>
                  <Icon name="calendarPlus" size={15} /> {t('加入日历')}
                </button>
              </div>
            </div>
            {role === 'drafts' && (
              <div className="notice notice--warn reader__banner">
                <Icon name="pen" size={16} />
                <span>{t('这是一封还没发出的草稿。')}</span>
                <button className="btn btn--small btn--primary" onClick={() => onEditDraft(messageId)}>
                  {t('继续编辑')}
                </button>
              </div>
            )}
            {remoteBlocked && (
              <div className="notice reader__banner">
                <Icon name="shield" size={16} />
                <span>{t('为了保护隐私，已拦下远程图片（它们可能用来追踪你是否读过邮件）。')}</span>
                <button className="btn btn--small" onClick={showImages}>
                  <Icon name="image" size={14} />
                  {t('显示图片')}
                </button>
              </div>
            )}
            <div className="thread">
              {thread.messages.map((m) => (
                <MessageCard
                  key={m.id}
                  m={m}
                  me={account?.email}
                  {...(m.id === current.id ? { bodyKey: 'thread.body' } : {})}
                  expanded={expanded.has(m.id) || thread.messages.length === 1}
                  onToggle={() =>
                    setExpanded((s) => {
                      const n = new Set(s);
                      if (n.has(m.id)) n.delete(m.id);
                      else n.add(m.id);
                      return n;
                    })
                  }
                  onReply={onReply}
                />
              ))}
            </div>
            <div className="reader__replies">
              <button className="btn" onClick={() => onReply('reply', thread.messages.at(-1)!.id)}>
                <Icon name="reply" size={16} /> {t('回复')}
              </button>
              <button className="btn" onClick={() => onReply('replyAll', thread.messages.at(-1)!.id)}>
                <Icon name="replyAll" size={16} /> {t('全部回复')}
              </button>
              <button className="btn" onClick={() => onReply('forward', thread.messages.at(-1)!.id)}>
                <Icon name="forward" size={16} /> {t('转发')}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/** Reader pane when no message is selected. */
export function ReaderEmpty({ unread }: { unread: number }) {
  return (
    <section className="pane reader reader--empty" aria-label={t('阅读邮件')}>
      <div className="empty">
        <div className="empty__art" aria-hidden>
          <Mascot />
        </div>
        <h2>{unread ? t('还有 {n} 封没读', { n: unread }) : t('都读完啦')}</h2>
        <p className="muted">{t('从左边选一封邮件来读。')}</p>
        <div className="empty__keys muted">
          <span>
            <kbd>J</kbd> <kbd>K</kbd> {t('上下切换')}
          </span>
          <span>
            <kbd>C</kbd> {t('写邮件')}
          </span>
          <span>
            <kbd>?</kbd> {t('全部快捷键')}
          </span>
        </div>
      </div>
    </section>
  );
}

/**
 * The unsubscribe button (for mail with List-Unsubscribe). One-click is instant; a web page is worked by the AI on the server, which
 * can take a minute; sending an unsubscribe email asks first; a page the AI can't finish opens in the browser.
 */
function UnsubscribeBar({ message }: { message: MessageDetail }) {
  const info = message.unsubscribe!;
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(info.done !== null);
  // The AI thinks they'd rather unsubscribe, or rated it junk or low (ads, newsletters): make the button stand out
  const promo = suggestsUnsubscribe(message.aiTag) || message.aiTag?.level === 'junk' || message.aiTag?.level === 'low';
  const run = async () => {
    setBusy(true);
    if (await runUnsubscribe(message.id)) setDone(true);
    setBusy(false);
  };
  if (done) {
    return (
      <div className="unsub unsub--done">
        <Icon name="check" size={15} /> {t('已退订这个发件人')}
      </div>
    );
  }
  return (
    <div className={`unsub ${promo ? 'unsub--promo' : ''}`}>
      <span className="unsub__text">{promo ? t('不想再收到这类邮件？') : t('这个发件人提供退订')}</span>
      <button className={`btn btn--small ${promo ? 'btn--primary' : ''}`} disabled={busy} onClick={() => void run()}>
        {busy ? (
          <>
            <span className="spinner" /> {info.oneClick ? t('正在退订…') : t('AI 正在退订，可能要一分钟…')}
          </>
        ) : (
          t('退订')
        )}
      </button>
    </div>
  );
}
